import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { categoryLabel } from "@qota/shared";
import { Amount, Avatar, Icon, IconButton } from "@qota/ui";
import { BalanceTile } from "../components/BalanceTile";
import { ExpenseSheet } from "../components/ExpenseSheet";
import { InviteSheet } from "../components/InviteSheet";
import { AppHeader } from "../components/AppHeader";
import { AppNav } from "../components/AppNav";
import { MobileShell } from "../components/MobileShell";
import { SettleSheet } from "../components/SettleSheet";
import { iconFor } from "../lib/category-icon";
import { getBalances } from "../lib/balances";
import { listExpenses } from "../lib/expenses";
import { getGroup } from "../lib/groups";
import { getStats } from "../lib/stats";
import { queryKeys } from "../lib/query-keys";
import { useSessionState } from "../lib/session-context";

/**
 * Schermata 03 — Gruppo.
 *
 * ── Un cruscotto, non un riepilogo ────────────────────────────────────────
 *
 * Prima c'erano due linguette, e la prima — «Quadro» — impilava tre cose di
 * peso identico: il totale speso, i saldi, le statistiche. Nessuna era più
 * grande delle altre, quindi entrando non si sapeva dove posare l'occhio. E la
 * domanda per cui si apre un gruppo — *devo o mi devono* — era la seconda riga
 * di un riquadro che si apriva con il totale speso, cioè la cifra meno
 * azionabile della schermata.
 *
 * Adesso è un cruscotto di tessere, ognuna **una porta**: riassume, e portando
 * alla schermata che approfondisce. I conti stanno in cima e si aprono sul
 * posto; spese e statistiche hanno una schermata ciascuna, dove non devono più
 * dividere l'attenzione con cose che rispondono ad altre domande.
 *
 * ── Cosa si perde, detto ──────────────────────────────────────────────────
 *
 * L'elenco delle spese non è più a un tocco dalla linguetta ma dietro una
 * tessera, che è lo stesso numero di tocchi. Le statistiche invece si
 * allontanano di un livello: era la coda del riepilogo, dove però non arrivava
 * nessuno senza scorrere.
 */
export function GroupScreen() {
  const { groupId } = useParams({ from: "/gruppi/$groupId" });
  const navigate = useNavigate();
  const { profile } = useSessionState();

  const [selectedExpenseId, setSelectedExpenseId] = useState<string | null>(null);
  const [settleWith, setSettleWith] = useState<string | null | undefined>(undefined);
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data: group } = useQuery({
    queryKey: queryKeys.group(groupId),
    queryFn: () => getGroup(groupId),
  });

  const { data: expenses } = useQuery({
    queryKey: queryKeys.expenses(groupId),
    queryFn: () => listExpenses(groupId),
  });

  const { data: balances } = useQuery({
    queryKey: queryKeys.balances(groupId),
    queryFn: () => getBalances(groupId),
  });

  const { data: stats } = useQuery({
    queryKey: queryKeys.stats(groupId, "6m", null),
    queryFn: () => getStats(groupId, "6m", null),
  });

  /**
   * Il membro che corrisponde a chi guarda.
   *
   * I saldi parlano di `memberId`, la sessione di `userId`: il ponte fra i due
   * è la lista dei membri. È l'invariante di Qota vista da qui — i conti non
   * conoscono gli account, e questa è l'unica riga che li mette in relazione.
   */
  const myMemberId =
    group?.members.find((member) => member.kind === "user" && member.userId === profile?.id)?.id ??
    null;

  const total = expenses?.reduce((acc, expense) => acc + expense.amountCents, 0) ?? 0;
  const recenti = expenses?.slice(0, 3) ?? [];

  const apriSpese = () => void navigate({ to: "/gruppi/$groupId/spese", params: { groupId } });
  const apriStatistiche = () =>
    void navigate({ to: "/gruppi/$groupId/statistiche", params: { groupId } });

  return (
    <MobileShell
      bottom={
        <AppNav
          value="gruppi"
          onAdd={() => void navigate({ to: "/gruppi/$groupId/spese/nuova", params: { groupId } })}
        />
      }
    >
      <AppHeader
        title={group?.name ?? "…"}
        subtitle={
          group
            ? `${group.memberCount} ${group.memberCount === 1 ? "persona" : "persone"}`
            : undefined
        }
        onBack={() => void navigate({ to: "/gruppi" })}
        actions={
          <div style={{ display: "flex", gap: 6 }}>
            <IconButton
              tone="light"
              size={38}
              label="Invita nel gruppo"
              onClick={() => setInviteOpen(true)}
            >
              <Icon name="user-plus" size={18} />
            </IconButton>
            <IconButton
              tone="light"
              size={38}
              label="Impostazioni del gruppo"
              onClick={() =>
                void navigate({ to: "/gruppi/$groupId/impostazioni", params: { groupId } })
              }
            >
              <Icon name="settings" size={18} />
            </IconButton>
          </div>
        }
      />

      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <BalanceTile
          balances={balances}
          myMemberId={myMemberId}
          onSettle={(withMemberId) => setSettleWith(withMemberId ?? null)}
        />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
          <Tessera label="Speso in tutto" onOpen={apriSpese}>
            <Amount cents={total} size={17} tone="neutral" />
            <div style={sottoStile}>
              {expenses?.length ?? 0} {expenses?.length === 1 ? "spesa" : "spese"}
            </div>
          </Tessera>

          <Tessera label="Questo mese" onOpen={apriStatistiche}>
            <Amount cents={meseCorrente(stats?.byMonth)} size={17} tone="neutral" />
            <Andamento months={stats?.byMonth ?? []} />
          </Tessera>
        </div>

        <Tessera label="Ultime spese" onOpen={apriSpese}>
          {recenti.length === 0 && <div style={sottoStile}>Ancora nessuna spesa</div>}

          {recenti.map((expense, index) => (
            <button
              key={expense.id}
              type="button"
              onClick={(event) => {
                // La tessera intera porta all'elenco: senza questo, aprire una
                // spesa aprirebbe anche la schermata sotto.
                event.stopPropagation();
                setSelectedExpenseId(expense.id);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                width: "100%",
                padding: "7px 0",
                border: "none",
                borderTop: index === 0 ? "none" : "1px solid var(--border-subtle)",
                background: "transparent",
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "var(--font-text)",
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  minWidth: 28,
                  borderRadius: "var(--radius-xs)",
                  background: "var(--surface-sunken)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon name={iconFor(expense.category)} size={15} />
              </span>

              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: "block",
                    fontSize: 12.5,
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {expense.description}
                </span>
                <span style={{ display: "block", ...sottoStile }}>
                  {expense.payerNames.join(", ")} · {formatDate(expense.spentAt)}
                </span>
              </span>

              <Amount cents={expense.amountCents} size={12.5} tone="neutral" />
            </button>
          ))}
        </Tessera>

        <Tessera label="Dove vanno i soldi" onOpen={apriStatistiche}>
          {(stats?.byCategory ?? []).slice(0, 3).map((entry) => (
            <div key={entry.category} style={{ marginTop: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span>{categoryLabel(entry.category)}</span>
                <Amount cents={entry.totalCents} size={12} tone="neutral" />
              </div>
              <div
                style={{
                  height: 5,
                  borderRadius: "var(--radius-pill)",
                  background: "var(--surface-sunken)",
                  overflow: "hidden",
                  marginTop: 4,
                }}
              >
                <div
                  style={{
                    width: `${Math.round(entry.share * 100)}%`,
                    height: "100%",
                    background: "var(--text-primary)",
                  }}
                />
              </div>
            </div>
          ))}

          {(stats?.byCategory.length ?? 0) === 0 && (
            <div style={sottoStile}>Servono delle spese per dirlo</div>
          )}
        </Tessera>

        {/* La tessera in cima mostra i conti che riguardano te; qui si va a
            vedere come sta messo ognuno, che con più persone è un'altra cosa. */}
        <Tessera
          label="Chi deve cosa"
          onOpen={() => void navigate({ to: "/gruppi/$groupId/saldi", params: { groupId } })}
        >
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            {(group?.members ?? []).slice(0, 6).map((member) => (
              <Avatar key={member.id} name={member.displayName} size={28} />
            ))}
          </div>
        </Tessera>
      </div>

      <InviteSheet
        groupId={groupId}
        members={group?.members ?? []}
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />

      <SettleSheet
        groupId={groupId}
        members={group?.members ?? []}
        open={settleWith !== undefined}
        withMemberId={settleWith ?? null}
        myMemberId={myMemberId}
        onClose={() => setSettleWith(undefined)}
      />

      <ExpenseSheet
        groupId={groupId}
        expenseId={selectedExpenseId}
        onClose={() => setSelectedExpenseId(null)}
      />
    </MobileShell>
  );
}

const sottoStile = { fontSize: 10.5, color: "var(--text-secondary)", marginTop: 2 } as const;

/**
 * Una tessera del cruscotto: riassume, e porta dove si approfondisce.
 *
 * Il segno «›» non è decorazione — dice che si apre qualcosa. Una tessera che
 * riassume senza portare da nessuna parte è un vicolo cieco, e questo cruscotto
 * è fatto di porte.
 */
function Tessera({
  label,
  onOpen,
  children,
}: {
  label: string;
  onOpen: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-card)",
        padding: 12,
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span
          style={{
            fontSize: "var(--text-label-size)",
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            color: "var(--text-secondary)",
          }}
        >
          {label}
        </span>
        <span style={{ color: "var(--text-tertiary)", display: "flex" }}>
          <Icon name="chevron-right" size={15} />
        </span>
      </div>

      <div style={{ marginTop: 4 }}>{children}</div>
    </div>
  );
}

/** L'andamento degli ultimi mesi, con quello corrente in evidenza. */
function Andamento({ months }: { months: { month: string; totalCents: number }[] }) {
  if (months.length === 0) return null;

  const max = Math.max(...months.map((entry) => entry.totalCents), 1);

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 20, marginTop: 6 }}>
      {months.slice(-6).map((entry, index, visibili) => (
        <span
          key={entry.month}
          style={{
            flex: 1,
            height: `${Math.max(8, Math.round((entry.totalCents / max) * 100))}%`,
            borderRadius: "2px 2px 0 0",
            background:
              index === visibili.length - 1 ? "var(--text-primary)" : "var(--border-strong)",
          }}
        />
      ))}
    </div>
  );
}

function meseCorrente(months: { month: string; totalCents: number }[] | undefined): number {
  return months?.[months.length - 1]?.totalCents ?? 0;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short" });
}
